import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useUpdateCaseOutput } from '@/hooks/useCases';
import type { FieldGroup } from '@/types/formSchema';
import { getNestedValue } from '@/utils/nestedAccordion';
import { setOutputAtPath } from '@/utils/outputPath';
import { collectPhoneFieldErrors, getPhoneFieldError } from '@/utils/phone';

interface UseOutputEditorParams {
  caseId?: string;
  /**
   * Groups used for phone/fax validation — the real output schema, NOT any
   * summary-merged variant. Both validation-on-change and the pre-save check run
   * against these.
   */
  validationGroups: FieldGroup[];
  /** Persisted output data used as the baseline (the "before" of the edit). */
  baselineOutput?: Record<string, unknown>;
  /** Output schema version sent on save (defaults to "1.0"). */
  schemaVersion?: string;
  /**
   * Fired after a successful save — callers use it to reconcile the persisted
   * disposition (e.g. `calculateDisposition(...)`).
   */
  onSaved?: () => void;
  /**
   * Fired when the user enters edit mode — e.g. to emit the manual-task-started
   * marker. Not fired by `applyFieldValue`.
   */
  onEditStart?: () => void;
}

/** Best-effort human-readable message from an API/mutation error. */
function extractErrorMessage(error: unknown): string {
  const e = error as { response?: { data?: { message?: string } }; message?: string };
  return e?.response?.data?.message || e?.message || 'Failed to save output data';
}

export interface OutputEditor {
  isEditing: boolean;
  /** The in-progress working copy (undefined when not editing). */
  editedOutput: Record<string, unknown> | undefined;
  /** The data the board should render: working copy while editing, else baseline. */
  workingOutput: Record<string, unknown>;
  fieldErrors: Record<string, string>;
  hasFieldErrors: boolean;
  /**
   * Bumped each time a Save is blocked by validation. The board watches this to
   * scroll to the first invalid field (a plain fieldErrors watch would also fire
   * on live keystroke validation, which must NOT scroll).
   */
  errorScrollNonce: number;
  isSaving: boolean;
  startEdit: () => void;
  cancelEdit: () => void;
  save: () => Promise<void>;
  /** Board onDataChange: updates the working copy and live-validates the changed field. */
  handleDataChange: (updated: Record<string, unknown>, fieldPath?: string) => void;
  /**
   * Enter edit mode (if not already) and stage `value` at the dotted `fieldPath` in the
   * working copy — used when accepting an AI-suggested value; the user still Saves.
   */
  applyFieldValue: (fieldPath: string, value: unknown) => void;
  /**
   * Persist a single `value` at the dotted `fieldPath` directly against the baseline —
   * used by one-off field affordances (accept an AI suggestion, edit a field from a
   * handoff action) that do NOT open an edit session (the board stays read-only).
   * `fieldPath` may address an entity element (`drug[0].coverage.copay`). Reconciles
   * the persisted disposition via `onSaved` on success. `successMessage` overrides the
   * accept-suggestion default toast.
   */
  commitFieldValue: (fieldPath: string, value: unknown, successMessage?: string) => Promise<void>;
}

/**
 * Shared output-editing state machine for the case Output board (BI-1246). Owns the
 * explicit Edit → working-copy → Save/Cancel flow, phone/fax validation (live on
 * change + blocking on save), and the persist call. View concerns (filter, expanded
 * accordions, scroll) live in `OutputBoardEditor`; disposition (live vs persisted)
 * is supplied by the caller. Used by both CaseSummaryView and HumanAuditTab so the
 * two output sections share one implementation.
 *
 * HIPAA: field values are PHI — never logged here; only the persist endpoint sees them.
 */
export function useOutputEditor({
  caseId,
  validationGroups,
  baselineOutput,
  schemaVersion = '1.0',
  onSaved,
  onEditStart,
}: UseOutputEditorParams): OutputEditor {
  const [isEditing, setIsEditing] = useState(false);
  const [editedOutput, setEditedOutput] = useState<Record<string, unknown> | undefined>(undefined);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [errorScrollNonce, setErrorScrollNonce] = useState(0);
  const updateCaseOutput = useUpdateCaseOutput();

  const workingOutput = useMemo<Record<string, unknown>>(
    () => (isEditing ? (editedOutput ?? baselineOutput ?? {}) : (baselineOutput ?? {})),
    [isEditing, editedOutput, baselineOutput],
  );

  const startEdit = useCallback(() => {
    // Deep-copy the persisted output into the working set so edits are isolated
    // until Save; Cancel discards the copy.
    setEditedOutput(JSON.parse(JSON.stringify(baselineOutput ?? {})));
    setIsEditing(true);
    onEditStart?.();
  }, [baselineOutput, onEditStart]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditedOutput(undefined);
    setFieldErrors({});
  }, []);

  const save = useCallback(async () => {
    if (!caseId) return;
    const data = editedOutput ?? {};
    // Validate phone/fax output fields before saving; block + highlight + scroll.
    const phoneErrors = collectPhoneFieldErrors(validationGroups, data);
    setFieldErrors(phoneErrors);
    if (Object.keys(phoneErrors).length > 0) {
      setErrorScrollNonce(n => n + 1);
      return;
    }
    try {
      await updateCaseOutput.mutateAsync({
        caseId,
        outputData: { schema_version: schemaVersion, data },
      });
      setIsEditing(false);
      setEditedOutput(undefined);
      setFieldErrors({});
      toast.success('Output data updated successfully');
      onSaved?.();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }, [caseId, editedOutput, validationGroups, schemaVersion, updateCaseOutput, onSaved]);

  const handleDataChange = useCallback(
    (updated: Record<string, unknown>, fieldPath?: string) => {
      setEditedOutput(updated);
      if (!fieldPath) return;
      // Live phone/fax validation: re-check this field on every change so the error
      // appears immediately and clears the moment the number becomes valid.
      const error = getPhoneFieldError(
        validationGroups,
        fieldPath,
        getNestedValue(updated, fieldPath),
      );
      setFieldErrors(prev => {
        if (!error && !prev[fieldPath]) return prev;
        const next = { ...prev };
        if (error) next[fieldPath] = error;
        else delete next[fieldPath];
        return next;
      });
    },
    [validationGroups],
  );

  const applyFieldValue = useCallback(
    (fieldPath: string, value: unknown) => {
      setIsEditing(true);
      setEditedOutput(prev => {
        const base = prev ?? (baselineOutput ? JSON.parse(JSON.stringify(baselineOutput)) : {});
        return setOutputAtPath(base, fieldPath, value);
      });
    },
    [baselineOutput],
  );

  const commitFieldValue = useCallback(
    async (fieldPath: string, value: unknown, successMessage = 'Accepted extracted value') => {
      if (!caseId) return;
      const data = setOutputAtPath(baselineOutput ?? {}, fieldPath, value);
      try {
        await updateCaseOutput.mutateAsync({
          caseId,
          outputData: { schema_version: schemaVersion, data },
        });
        toast.success(successMessage);
        onSaved?.();
      } catch (error) {
        toast.error(extractErrorMessage(error));
      }
    },
    [caseId, baselineOutput, schemaVersion, updateCaseOutput, onSaved],
  );

  return {
    isEditing,
    editedOutput,
    workingOutput,
    fieldErrors,
    hasFieldErrors: Object.keys(fieldErrors).length > 0,
    errorScrollNonce,
    isSaving: updateCaseOutput.isPending,
    startEdit,
    cancelEdit,
    save,
    handleDataChange,
    applyFieldValue,
    commitFieldValue,
  };
}
